import React, { useEffect, useState, useRef } from "react";
import Table from "../../../components/common/table";
import { useSelector, useDispatch } from "react-redux";
import { Pagination, message, Typography, Tooltip, Tag } from "antd";
import View from "../../../assets/icons/view.svg";
import Trash from "../../../assets/icons/trash.svg";
import { resetText } from "../../../store/slices/textSlice";
import {
  deleteBusiness,
  getNewBusinesses,
  getBusinessTypes,
} from "../../../api/business";
import DeleteMemberModal from "../../../components/common/modalMember";
import { Link } from "react-router-dom";
import Filter from "../../../components/common/filter";
import { ReactComponent as FilterIcon } from "../../../assets/icons/filterIcon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refreshIcon.svg";

const NewBusiness = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const text = useSelector((state) => state?.text?.value);
  const [newBusinesses, setNewBusinesses] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isBusinessTypeLoading, setIsBusinessTypeLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});

  const { Text } = Typography;
  const statusOptions = ["Approved", "Pending"];
  const staffSizesOptions = [
    "0 - 50",
    "50 - 100",
    "100 - 150",
    "150 - 200",
    "200 - 250",
    "300+",
  ];

  const tabOptions = {
    Status: statusOptions,
    "Business Type": businessTypes,
    "Staff Size": staffSizesOptions,
  };

  const handleOptionClick = (option) => {
    setSelectedOptions((prevSelectedOptions) => {
      if (activeTab === "Staff Size" || activeTab === "Status") {
        return {
          ...prevSelectedOptions,
          [activeTab]: [option],
        };
      } else if (activeTab === "Business Type") {
        const id = option._id || option; // Get the _id for Business Type, otherwise use the option directly
        const currentOptions = prevSelectedOptions[activeTab] || [];

        if (currentOptions.includes(id)) {
          return {
            ...prevSelectedOptions,
            [activeTab]: currentOptions.filter((item) => item !== id),
          };
        } else {
          return {
            ...prevSelectedOptions,
            [activeTab]: [...currentOptions, id],
          };
        }
      } else {
        const currentOptions = prevSelectedOptions[activeTab] || [];
        if (currentOptions.includes(option)) {
          return {
            ...prevSelectedOptions,
            [activeTab]: currentOptions.filter((item) => item !== option),
          };
        } else {
          return {
            ...prevSelectedOptions,
            [activeTab]: [...currentOptions, option],
          };
        }
      }
    });
  };

  const resetFilters = (setIsDropdownOpen) => {
    setSelectedOptions({});
    setActiveTab(null);
    setIsDropdownOpen(false);
    fetchNewBusinesses(null);
  };

  const tabs = [
    { key: "tab1", label: <FilterIcon /> },
    {
      key: "tab2",
      label: (
        <Tooltip title="Filter By">
          <Text ellipsis style={{ maxWidth: 80 }}>
            Filter By
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "Status",
      label: (
        <Tooltip title="Status">
          <Text ellipsis style={{ maxWidth: 80 }}>
            Status
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "Business Type",
      label: (
        <Tooltip title="Business Type">
          <Text ellipsis style={{ maxWidth: 100 }}>
            Business Type
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "Staff Size",
      label: (
        <Tooltip title="Staff Size">
          <Text ellipsis style={{ maxWidth: 80 }}>
            Staff Size
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "tab6",
      label: (
        <>
          <RefreshIcon width={40} />{" "}
          <Tooltip title="Reset Filter">
            <Text ellipsis style={{ maxWidth: 100, color: "red" }}>
              Reset Filter
            </Text>
          </Tooltip>
        </>
      ),
    },
  ];

  const constructQueryString = (filters) => {
    const queryParams = [];

    if (filters?.["Status"]?.length > 0) {
      queryParams.push(
        `status=${encodeURIComponent(
          JSON.stringify(filters["Status"][0]?.toUpperCase())
        )}`
      );
    }

    if (filters?.["Business Type"]?.length > 0) {
      queryParams.push(
        `businessTypes=${encodeURIComponent(
          JSON.stringify(filters["Business Type"])
        )}`
      );
    }

    if (filters?.["Staff Size"]?.length > 0) {
      queryParams.push(
        `staffSize=${encodeURIComponent(JSON.stringify(filters["Staff Size"]))}`
      );
    }

    return queryParams.length > 0 ? queryParams.join("&") : "";
  };

  const fetchNewBusinesses = async (selectedOptions) => {
    const queryString = constructQueryString(selectedOptions);
    setLoading(true);
    const payload = {
      pageNumber: currentPage,
      itemPerPage: pageSize,
      text: text,
    };
    const res = await getNewBusinesses(payload, queryString);
    setNewBusinesses(res);
    setLoading(false);
  };

  useEffect(() => {
    dispatch(resetText());
    fetchBusinessTypes();
  }, []);

  useEffect(() => {
    fetchNewBusinesses(selectedOptions);
  }, [currentPage, pageSize, text]);

  const onShowDeleteModalHandler = (record) => {
    setIsDeleteModalOpen(true);
    setSelectedCard(record);
  };

  const columns = [
    {
      title: "Business Name",
      dataIndex: "businessName",
      key: "businessName",
    },
    {
      title: "Business Type",
      dataIndex: ["businessType", "name"],
      key: "businessTypeName",
    },
    {
      title: "Staff Size",
      dataIndex: "staffSize",
      key: "staffSize",
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
    },
    {
      title: "Address",
      dataIndex: "businessAddress",
      render: (businessLocation, record) =>
        businessLocation?.[0]?.businessAddress ??
        record?.businessAddress ??
        "N/A",

      key: "businessAddress",
    },
    {
      title: "Status",
      dataIndex: 'status',
      render: (_, record) => {
        if (record?.isSuspended) {
          return <Tag color="red">Suspended</Tag>;
        }
        if (record?.isSubscriptionPaused) {
          return <Tag color="orange">Paused</Tag>;
        }
        if (record?.isApproved) {
          return <Tag color="green">Approved</Tag>;
        }
        return <Tag color="blue">Pending</Tag>;
      },
      key: "businessAddress",
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <div className="flex space-x-2">
          <Link to={`/main/business-request-detail/${record?._id}`}>
            <div className="w-4 m-auto grid justify-center h-4 pt-1 rounded-full bg-lightLavender cursor-pointer">
              <img src={View} alt="View" />
            </div>
          </Link>
          <div
            className="w-4 m-auto grid justify-center h-4 pt-1 rounded-full bg-lightCrimson cursor-pointer"
            onClick={() => onShowDeleteModalHandler(record)}
          >
            <img src={Trash} alt="Delete" />
          </div>
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (current, size) => {
    setPageSize(size);
  };

  const fetchBusinessTypes = async () => {
    setIsBusinessTypeLoading(true);
    try {
      const res = await getBusinessTypes();
      setBusinessTypes(res);
    } catch (error) {
    } finally {
      setIsBusinessTypeLoading(false);
    }
  };

  const itemRender = (page, type, originalElement) => {
    if (type === "page") {
      return (
        <div
          className={`rounded ${
            currentPage === page
              ? "bg-primaryPurple text-white"
              : "bg-white text-black"
          }`}
          style={{ cursor: "pointer" }}
        >
          {page}
        </div>
      );
    }
    return originalElement;
  };

  const deleteBusinessById = async () => {
    const res = await deleteBusiness(selectedCard?._id);
    if (res) {
      fetchNewBusinesses(null);
      message.success("Business Deleted Successfully!");
    }
  };

  const onDeleteHandler = () => {
    setIsDeleteModalOpen(false);
    deleteBusinessById();
    setSelectedCard(null);
  };

  return (
    <>
      <div className="pt-2 pl-2 text-lg font-bold text-black">Vendors</div>
      <Filter
        tabs={tabs}
        tabOptions={tabOptions}
        activeTab={activeTab}
        selectedOptions={selectedOptions}
        setActiveTab={setActiveTab}
        handleOptionClick={handleOptionClick}
        resetFilters={resetFilters}
        fetchNewBusinesses={fetchNewBusinesses}
      />

      <div className="min-h-screen flex items-start justify-start pt-0">
        <Table
          columns={columns}
          data={newBusinesses?.items}
          loading={loading}
        />
      </div>

      <div className="flex justify-center items-center my-2">
        <Pagination
          current={currentPage}
          total={newBusinesses?.totalItems}
          pageSize={pageSize}
          onShowSizeChange={handleSizeChange}
          pageSizeOptions={[10, 20, 30, 40]}
          onChange={(e) => {
            handlePageChange(e);
          }}
          itemRender={itemRender}
          responsive={true}
          className="custom-pagination"
        />
      </div>

      {isDeleteModalOpen && (
        <DeleteMemberModal
          title="Delete Business"
          text="Are you sure you want to delete this business?"
          onCancel={() => setIsDeleteModalOpen(false)}
          onOk={onDeleteHandler}
        />
      )}
    </>
  );
};

export default NewBusiness;
